import { useEffect, useRef, useState } from 'react';

import clsx from 'clsx';

//Hooks
import useUser from '@/utils/hooks/swr/useUser';
import useTranslation from '@/utils/hooks/translation/useTranslation';

//Utils
import { deleteApi, post } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';
import useIntersectionObserver from '@/utils/hooks/utils/useIntersectionObserver';

import PropTypes from 'prop-types';

//Endpoints
import { COMMENT_DELETE, COMMENT_MARK_AS_READ } from '@/lib/routes/apiEndpoints';

import Button from '@/components/base/Button';
//Components
import Comment from '@/components/base/Comment';
import CommentLoading from '@/components/base/Comment/CommentLoading';
import Loader from '@/components/base/Loader';
import RepliesList from '@/components/composite/Comments/RepliesList';
import { Illustration } from '@/components/composite/Illustration';
//Composite
import CommentDeleteModal from '@/components/composite/Modals/CommentDeleteModal';

//image
import illustrationEn from './../../../../public/images/illustrations/illustrations-empty-en.webp';
import illustrationEs from './../../../../public/images/illustrations/illustrations-empty-es.webp';
import illustrationFr from './../../../../public/images/illustrations/illustrations-empty-fr.webp';

const deleteCommentStateInitial = {
    commentId: null,
    postId: null,
    isOpenModal: false,
    isLoading: false,
    isError: false
};

const CommentsList = ({
    dataComments,
    socialProfile,
    isLoading,
    isLoadingMore,
    isLoadMore,
    loadMore,
    markAllAsReadState,
    setMarkAllAsRead,
    handleMarkAllAsRead = () => {
        /** */
    },
    displayPost = false,
    activeFormReply = null,
    onClickOnPost = () => {
        /** */
    },
    setActiveFormReply = () => {
        /** */
    },
    mutateComments = () => {
        /** */
    },
    className,
    repliesClassName = '',
    shouldDisplayIllustration = true,
    isWinningComments = false,
    variant = 'default'
}) => {
    const [replyToAdd, setReplyToAdd] = useState(null);
    const [deleteCommentState, setDeleteCommentState] = useState(deleteCommentStateInitial);
    const { t } = useTranslation('commons');
    const { user } = useUser({
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    const deleteComment = async () => {
        setDeleteCommentState({ ...deleteCommentState, isLoading: true, isError: false });
        try {
            await deleteApi(
                injectEndpointUrlParams(COMMENT_DELETE, {
                    SPID: socialProfile.id,
                    mediaID: deleteCommentState.postId,
                    commentID: deleteCommentState.commentId
                })
            );
            mutateComments(
                (prevState) =>
                    prevState?.map((page) => ({
                        ...page,
                        data: page.data.filter(
                            (comment) => comment.id !== deleteCommentState.commentId
                        ),
                        random_picker_winners: Array.isArray(page?.random_picker_winners)
                            ? page?.random_picker_winners?.filter(
                                  (comment) => comment.id !== deleteCommentState.commentId
                              )
                            : page?.random_picker_winners,
                        summary: {
                            ...page.summary,
                            total_count: page.summary.total_count - 1
                        }
                    })),
                false
            );
            setDeleteCommentState(deleteCommentStateInitial);
        } catch (e) {
            setDeleteCommentState({ ...deleteCommentState, isLoading: false, isError: true });
        }
    };

    const markAsRead = async (commentId, mediaId) => {
        try {
            await post(
                injectEndpointUrlParams(COMMENT_MARK_AS_READ, {
                    SPID: socialProfile?.id,
                    mediaID: mediaId,
                    commentID: commentId
                })
            );
            mutateComments(
                (prevState) =>
                    prevState?.map((page) => ({
                        ...page,
                        data: page.data.map((comment) => {
                            if (comment.id === commentId) {
                                return {
                                    ...comment,
                                    is_read: true
                                };
                            }

                            return comment;
                        }),
                        random_picker_winners: Array.isArray(page?.random_picker_winners)
                            ? page?.random_picker_winners.map((comment) => {
                                  if (comment.id === commentId) {
                                      return {
                                          ...comment,
                                          is_read: true
                                      };
                                  }

                                  return comment;
                              })
                            : page?.random_picker_winners,
                        summary:
                            typeof page.summary.unread_count === 'number'
                                ? {
                                      ...page.summary,
                                      unread_count: page.summary.unread_count - 1
                                  }
                                : page.summary
                    })),
                false
            );

            return {
                success: true
            };
        } catch (e) {
            return {
                success: false
            };
        }
    };

    if (isLoading) {
        return (
            <div className={clsx('pt-5', className)}>
                {[...Array(4).keys()].map((index) => (
                    <CommentLoading key={index} displayPost={displayPost} />
                ))}
            </div>
        );
    }

    if (
        dataComments &&
        dataComments.length === 1 &&
        !dataComments?.[0]?.data?.length &&
        shouldDisplayIllustration
    ) {
        return (
            <div className="flex flex-1 flex-col items-center justify-center space-y-2 text-icoGray-500 dark:text-icoDarkMode-wolf">
                <Illustration
                    src={{ en: illustrationEn, fr: illustrationFr, es: illustrationEs }}
                />
                <span className="text-center">{t('conversations:comments_tracker.notice')}</span>
            </div>
        );
    }

    return (
        <div className={clsx('relative pt-5', className)}>
            {dataComments[0]?.summary?.unread_count ? (
                <div className="mb-1">
                    <button
                        data-testid="mark-all-as-read-button"
                        className={clsx(
                            'absolute right-0 top-0 flex items-center text-xs text-icoBlue',
                            {
                                'opacity-50': markAllAsReadState?.isSubmitting
                            }
                        )}
                        disabled={markAllAsReadState?.isSubmitting}
                        onClick={handleMarkAllAsRead}>
                        {markAllAsReadState?.isSubmitting ? (
                            <Loader className="mr-1 h-3 w-3" />
                        ) : null}
                        {t('comment.mark_all_read')}
                    </button>
                </div>
            ) : null}
            {dataComments?.map((comments) => {
                return comments?.data?.map((comment) => (
                    <Comment
                        key={comment.id}
                        variant={variant}
                        socialProfile={socialProfile}
                        comment={comment}
                        user={user}
                        activeFormReply={activeFormReply}
                        handleActiveFormReply={(status) => {
                            setActiveFormReply(
                                status === 'open'
                                    ? {
                                          currentComment: comment,
                                          currentCommentId: comment.id,
                                          parent: comment
                                      }
                                    : null
                            );
                        }}
                        markAsRead={markAsRead}
                        deleteComment={() => {
                            setDeleteCommentState({
                                ...deleteCommentState,
                                postId: comment?.media_id,
                                commentId: comment?.id,
                                isOpenModal: true
                            });
                        }}
                        handleReplyToAdd={(reply) => {
                            setReplyToAdd({
                                commentId: comment.id,
                                reply
                            });
                        }}
                        displayPost={displayPost}
                        onClickOnPost={onClickOnPost}
                        className={clsx(
                            'mb-3 rounded-md p-4 dark:border-icoDarkMode-anthracite sm:mb-10',
                            {
                                'border-4 border-white': variant === 'default',
                                'w-full border dark:border-icoDarkMode-beton':
                                    variant === 'socialInbox'
                            }
                        )}
                        relatedComments={dataComments}
                        isWinningComments={isWinningComments}
                        replies={
                            <RepliesList
                                className={repliesClassName}
                                variant={variant}
                                comment={comment}
                                socialProfile={socialProfile}
                                mutateComments={mutateComments}
                                activeFormReply={activeFormReply}
                                setActiveFormReply={setActiveFormReply}
                                replyToAdd={replyToAdd}
                                setReplyToAdd={setReplyToAdd}
                                markRepliesAsRead={markAllAsReadState.markRepliesAsRead}
                                setMarkAllAsRead={setMarkAllAsRead}
                            />
                        }
                    />
                ));
            })}
            {isLoadMore && (
                <LoadMoreButton loadMoreComments={loadMore} isLoadingMore={isLoadingMore} />
            )}
            <CommentDeleteModal
                isOpen={deleteCommentState.isOpenModal}
                toggle={() => {
                    setDeleteCommentState({
                        ...deleteCommentState,
                        isOpenModal: false
                    });
                }}
                deleteComment={deleteComment}
                isLoading={deleteCommentState.isLoading}
                isError={deleteCommentState.isError}
            />
        </div>
    );
};

CommentsList.propTypes = {
    dataComments: PropTypes.array,
    socialProfile: PropTypes.object,
    isLoading: PropTypes.bool,
    isLoadingMore: PropTypes.bool,
    isLoadMore: PropTypes.bool,
    loadMore: PropTypes.func,
    displayPost: PropTypes.bool,
    activeFormReply: PropTypes.object,
    setActiveFormReply: PropTypes.func,
    mutateComments: PropTypes.func,
    className: PropTypes.string,
    repliesClassName: PropTypes.string,
    onClickOnPost: PropTypes.func,
    shouldDisplayIllustration: PropTypes.bool,
    isWinningComments: PropTypes.bool,
    variant: PropTypes.oneOf(['default', 'socialInbox'])
};

const LoadMoreButton = ({ loadMoreComments, isLoadingMore }) => {
    const { t } = useTranslation('commons');
    const loadMoreButtonRef = useRef();
    const loadMoreInViewPort = useIntersectionObserver(loadMoreButtonRef, '0px');

    useEffect(() => {
        if (!isLoadingMore && loadMoreInViewPort) {
            loadMoreComments();
        }
    }, [isLoadingMore, loadMoreInViewPort, loadMoreComments]);

    return (
        <Button
            variant="secondary"
            ref={loadMoreButtonRef}
            isLoading={isLoadingMore}
            onClick={loadMoreComments}
            className="w-full bg-white">
            {t('load_more')}
        </Button>
    );
};

LoadMoreButton.propTypes = {
    loadMoreComments: PropTypes.func,
    isLoadingMore: PropTypes.bool
};

export default CommentsList;
